import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Anchor,
    CheckCircle2,
    ShieldCheck,
    Truck,
    Star,
    ArrowRight,
} from "lucide-react";

const CRUISE_LINES = [
    "Carnival Cruise Line",
    "Disney Cruise Line",
    "Royal Caribbean",
    "MSC Cruises",
    "Norwegian Cruise Line",
];

function SEO() {
    useEffect(() => {
        const prevTitle = document.title;
        document.title =
            "Port Canaveral Cruise Scooter Rental · $99 Concierge Delivery";
        const metaDesc = document.querySelector('meta[name="description"]');
        const prevDesc = metaDesc?.getAttribute("content");
        metaDesc?.setAttribute(
            "content",
            "Rent a mobility scooter for your Port Canaveral cruise. $99 flat-rate concierge delivery — round-trip drop-off & pickup for Carnival, Disney, Royal Caribbean, MSC & Norwegian. Book online.",
        );
        return () => {
            document.title = prevTitle;
            if (prevDesc) metaDesc?.setAttribute("content", prevDesc);
        };
    }, []);
    return null;
}

export default function PortCanaveralConcierge() {
    return (
        <div data-testid="concierge-landing-page">
            <SEO />

            {/* HERO */}
            <section className="relative overflow-hidden bg-[color:var(--brand-navy)] text-white">
                <img
                    src="https://images.unsplash.com/photo-1580541631950-7282082b53ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjcnVpc2UlMjBzaGlwJTIwZG9ja3xlbnwwfHx8fDE3ODU4MTc1Mzh8MA&ixlib=rb-4.1.0&q=85"
                    alt="Cruise ship at Port Canaveral"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--brand-navy)] via-[color:var(--brand-navy)]/85 to-[color:var(--brand-navy)]/40" />
                <div className="relative mx-auto grid max-w-7xl gap-10 px-4 pb-24 pt-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pt-28">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="lg:col-span-8"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-gold)]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-gold)]">
                            <Anchor className="h-3.5 w-3.5" />
                            Concierge Service · Port Canaveral
                        </div>
                        <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                            Port Canaveral Cruise{" "}
                            <span className="text-[color:var(--brand-gold)]">
                                Scooter Rental
                            </span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base font-medium leading-relaxed text-white/95 sm:text-lg">
                            A premium round-trip mobility scooter service for
                            cruise passengers sailing from Port Canaveral —
                            hand-delivered before boarding, picked up after
                            debarkation.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <Link
                                to="/scooters/pride-victory-10"
                                data-testid="concierge-hero-book"
                                className="hero-cta pill-btn inline-flex items-center gap-2 text-white"
                            >
                                Book Concierge Delivery
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                        className="lg:col-span-4"
                    >
                        <div className="rounded-2xl border border-[color:var(--brand-gold)]/40 bg-white/5 p-8 backdrop-blur">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-gold)]">
                                Flat Rate
                            </div>
                            <div className="mt-2 font-display text-6xl font-extrabold text-white">
                                $99
                            </div>
                            <div className="mt-1 text-sm text-slate-300">
                                All-inclusive round-trip · min 4-day rental
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* WHAT'S INCLUDED */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div className="mb-12 max-w-2xl">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                        What&apos;s Included
                    </div>
                    <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-4xl">
                        White-glove service from door to deck
                    </h2>
                </div>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                    {[
                        {
                            icon: Truck,
                            title: "Terminal drop-off",
                            body: "We hand-deliver a fully-charged scooter directly to your cruise terminal or nearby hotel before boarding.",
                        },
                        {
                            icon: Anchor,
                            title: "Round-trip pickup",
                            body: "We meet you after debarkation at your terminal or hotel to collect the scooter. No logistics on your end.",
                        },
                        {
                            icon: ShieldCheck,
                            title: "Sanitized & serviced",
                            body: "Every unit is deep-cleaned, tested, and fully-charged before each rental.",
                        },
                        {
                            icon: Star,
                            title: "4-day minimum",
                            body: "The concierge flat rate covers the entire duration of a 4+ day cruise rental — no daily add-ons.",
                        },
                    ].map((f) => (
                        <div
                            key={f.title}
                            data-testid={`concierge-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="card-surface p-8"
                        >
                            <f.icon className="h-6 w-6 text-[color:var(--brand-gold)]" />
                            <h3 className="mt-3 font-display text-lg font-extrabold text-[color:var(--brand-navy)]">
                                {f.title}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                {f.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="bg-white">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                    <div className="mb-12 max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                            How It Works
                        </div>
                        <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-4xl">
                            Three steps to a stress-free cruise
                        </h2>
                    </div>
                    <ol className="grid gap-6 md:grid-cols-3">
                        {[
                            {
                                n: "1",
                                title: "Book online",
                                body: "Pick your cruise dates (minimum 4 days) and add your cruise line + ship name at checkout.",
                            },
                            {
                                n: "2",
                                title: "We deliver to the port",
                                body: "Our team meets you at the terminal or your pre-cruise hotel with a fully-charged scooter and a quick walkthrough.",
                            },
                            {
                                n: "3",
                                title: "We pick up when you dock",
                                body: "Walk off the ship, hand off the scooter to our team, and enjoy the rest of your trip.",
                            },
                        ].map((s) => (
                            <li key={s.n} className="card-surface p-8">
                                <div className="grid h-11 w-11 place-items-center rounded-full bg-[color:var(--brand-navy)] font-display text-xl font-extrabold text-white">
                                    {s.n}
                                </div>
                                <h3 className="mt-4 font-display text-xl font-extrabold text-[color:var(--brand-navy)]">
                                    {s.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                    {s.body}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* CRUISE LINES */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div className="mb-12 max-w-2xl">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                        Cruise Lines Supported
                    </div>
                    <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-4xl">
                        We coordinate with every Port Canaveral operator
                    </h2>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {CRUISE_LINES.map((line) => (
                        <div
                            key={line}
                            data-testid={`cruise-line-card-${line.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="card-surface flex items-center gap-3 p-5"
                        >
                            <CheckCircle2 className="h-5 w-5 shrink-0 text-[color:var(--brand-green)]" />
                            <span className="text-sm font-semibold text-[color:var(--brand-navy)]">
                                {line}
                            </span>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-slate-50">
                <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
                    <div className="mb-10 max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                            Cruise FAQ
                        </div>
                        <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-4xl">
                            Answers for cruise passengers
                        </h2>
                    </div>
                    <div className="space-y-6">
                        {[
                            {
                                q: "Do I need to be at the terminal for the drop-off?",
                                a: "Yes — we deliver on the morning of your embarkation. We coordinate a 30-minute window with you and can meet you curbside at the terminal or at a nearby hotel if you're arriving the night before.",
                            },
                            {
                                q: "What if my ship is delayed?",
                                a: "No problem — we track your ship's status on debarkation day and adjust our pickup accordingly. Just let us know if plans change.",
                            },
                            {
                                q: "Can I bring the scooter onto the ship?",
                                a: "Cruise lines have their own policies for personal mobility devices. The Pride Victory 10 disassembles for transport and is accepted by most operators — please confirm with your cruise line in advance.",
                            },
                            {
                                q: "Why is there a 4-day minimum?",
                                a: "Port Canaveral is outside our complimentary Orlando delivery area and requires dedicated round-trip service. Most cruises departing from Port Canaveral are 4 days or longer, so we've designed our concierge service to match the needs of the majority of cruise travelers while ensuring reliable, on-time delivery and pickup.",
                            },
                        ].map((item) => (
                            <div
                                key={item.q}
                                className="card-surface p-8"
                                data-testid={`concierge-faq-${item.q.slice(0, 20).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                                <h3 className="font-display text-lg font-extrabold text-[color:var(--brand-navy)]">
                                    {item.q}
                                </h3>
                                <p className="mt-2 leading-relaxed text-slate-600">
                                    {item.a}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div
                    className="relative overflow-hidden rounded-3xl bg-[color:var(--brand-navy)] p-10 sm:p-14"
                    data-testid="concierge-final-cta"
                >
                    <div className="grain absolute inset-0" />
                    <div className="relative flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
                        <div className="max-w-2xl">
                            <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-gold)]">
                                Ready to sail?
                            </div>
                            <h3 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                                Book your Port Canaveral concierge delivery.
                            </h3>
                            <p className="mt-3 text-slate-300">
                                $99 flat rate · minimum 4-day rental · every
                                cruise line supported.
                            </p>
                        </div>
                        <Link
                            to="/scooters/pride-victory-10"
                            data-testid="concierge-cta-book"
                            className="hero-cta pill-btn inline-flex items-center gap-2 text-white"
                        >
                            Reserve Now
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
